import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { AlertController, Platform, PopoverController } from '@ionic/angular';
import { AuthService } from '../services/auth.service';
import { CaseService } from '../services/case.service';
import { CommonService } from '../services/common.service';
import { ReinitService } from '../services/reinit.service';
import { Subscription } from 'rxjs';
import { HelpContentComponent } from './help-content/help-content.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit {
  portalSettingsSubscription: Subscription;
  loginForm: FormGroup;
  dpa_check: string;
  dynamicLogin: number = 0;
  newField;
  settings: any;
  fieldList = {
    case_ref: {
      key: 'case_ref',
      label: this.translate.instant('LOGIN.CASE_REF'),
      content: '',
    },
    surname: {
      key: 'surname',
      label: this.translate.instant('LOGIN.SURNAME'),
      content: '',
    },
  };
  fieldKeys;
  isMobile = false;
  title = 'Lateral Debtor Portal';
  logo_url = '';
  logo_size = '60px';
  background_url = '';
  client = '';
  loginBtnDisabled = false;
  datepicker_date_format = 'DD/MM/YYYY';
  showInfoIcon = false;
  login_header_message: string;
  login_footer_message: string;
  login_footer_colour_code: string;
  showNotification = false;
  agreement_acceptance: boolean;
  agreement_acceptance_label: string;
  isAgreed: boolean = false;
  state_disclosures: string;
  showMultipleFieldsMessage: boolean = false;
  full_title_login = null;
  swapFields: {
    primary_field: string;
    secondary_field: string;
    secondary_field_label: string;
  };
  defaultLabels = {
    case_id: 'Case Id',
    case_ref: 'Case Reference',
  };
  checkTranslation = false;
  login_button_label: string;
  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private platform: Platform,
    private popoverController: PopoverController,
    private reinitService: ReinitService,
    private alertController: AlertController,
    private translate: TranslateService,
    private caseService: CaseService
  ) {
    this.dpa_check = '';
    this.fieldKeys = Object.keys(this.fieldList);
    this.login_header_message = '';
    this.login_footer_message = '';
    this.login_footer_colour_code = '';
    this.agreement_acceptance = false;
    this.agreement_acceptance_label = '';
    this.state_disclosures = '';
    this.full_title_login = null;
    this.login_button_label = '';

    // Load settings synchronously from cache in constructor to prevent flash
    const cachedSettings = localStorage.getItem('portal_settings');
    if (cachedSettings) {
      try {
        const settings = JSON.parse(cachedSettings);
        this.applyPortalSettings(settings);
      } catch (e) {
        console.error('Failed to parse cached settings', e);
      }
    }
  }

  ngOnInit() {
    // Check for navigation redirects
    const cachedSettings = localStorage.getItem('portal_settings');
    if (cachedSettings) {
      const settings = JSON.parse(cachedSettings);
      
      if (settings?.additional_settings?.before_login?.before_login_allow_multistep === "yes" && 
          settings?.additional_settings?.before_login?.multistep_notification?.main_content?.textarea !== "" && 
          !localStorage.getItem('before_login_completed')) {
        this.router.navigate(['/login/notifications'], {
          queryParams: { url: this.route.snapshot.queryParams.url || '/home' }
        });
        return;
      }

      if (this.caseService.isClient('oriel')) {
        this.defaultLabels = {
          case_id: 'Oriel Reference',
          case_ref: 'Client Reference',
        };
      } 
      
      if (settings?.additional_settings?.custom_notifications?.before_login && 
          !localStorage.getItem('before_login_completed')) {
        this.router.navigate(['/login/notifications'], {
          queryParams: { url: this.route.snapshot.queryParams.url || '/home' }
        });
        return;
      }
    }

    this.initForm();
  }
  ngOnDestroy(): void {
    if (this.portalSettingsSubscription) {
      this.portalSettingsSubscription.unsubscribe();
    }
  }

  ionViewWillEnter() {
    this.isMobile = this.platform.is('mobile');
    this.wrapFooterAndSiblings();
    if (localStorage.getItem('dynamicLogin')) {
      this.dynamicLogin = parseInt(localStorage.getItem('dynamicLogin'));
      // localStorage.removeItem('dynamicLogin')
      localStorage.removeItem('default_webform_configured');
    }
    this.route.queryParams.subscribe((params) => {
      if (params && params.dynamic) {
        this.dynamicLogin = params.dynamic;
      }
    });
    if (this.router.url == '/viewmycase') {
      this.dynamicLogin = 1;
      localStorage.setItem('from_viewmycase', this.dynamicLogin.toString());
    } else {
      localStorage.removeItem('from_viewmycase');
    }

    this.fetchSettings();
  }

  initForm() {
    this.loginForm = this.formBuilder.group({
      dob: [
        '',
        Validators.pattern(
          '((0[1-9]|[12][0-9]|3[01])[/](0[1-9]|1[012])[/](?:19|20)[0-9]{2}$)|((0[1-9]|1[012])[/](0[1-9]|[12][0-9]|3[01])[/](?:19|20)[0-9]{2}$)'
        ),
      ],
      postcode: [''],
      surname: [''],
      name: [''],
      full_name: [''],  /** @see LU-182 give support of full name for debtor portal login */
      case_id: [''],
      case_ref: [''],
      mobile_no: [''],
      ssn: ['',[Validators.minLength(4), Validators.maxLength(4), Validators.pattern('[0-9]{4}')]],
      postcode_new: ['', [Validators.minLength(5), Validators.maxLength(5)]],
      debtor_email: ['', [Validators.email]],
    });
    if (localStorage.getItem('dpa_check')) {
      this.dpa_check = localStorage.getItem('dpa_check');
    }
  }

  updateField(key = '') {
    if (this.dynamicLogin != 1 || !this.swapFields) return;
    const { primary_field, secondary_field, secondary_field_label } = this.swapFields;

    const fields = {
      [primary_field]: {
        alt: secondary_field,
        key: primary_field,
        label:
          this.fieldList[primary_field]?.label ||
          this.toCamelCase(primary_field),
      },
      [secondary_field]: {
        alt: primary_field,
        key: secondary_field,
        label:
          secondary_field_label ||
          this.fieldList[secondary_field]?.label ||
          this.toCamelCase(secondary_field),
      },
    };

    if (!key) {
      if (
        this.fieldKeys.includes(primary_field) &&
        !this.fieldKeys.includes(secondary_field)
      ) {
        this.newField = fields[secondary_field];
      } else if (
        this.fieldKeys.includes(secondary_field) &&
        !this.fieldKeys.includes(primary_field)
      ) {
        this.newField = fields[primary_field];
      }
      return;
    }

    const toggleField = (field, altKey) => {
      if (!this.fieldKeys.includes(field.key)) {
        this.fieldList[field.key] = field;
        const index = this.fieldKeys.indexOf(altKey);
        if (index !== -1) this.fieldKeys.splice(index, 1, field.key);
        this.newField = fields[altKey];
        this.loginForm.controls[altKey].setValue('');
      }
    };

    if ([primary_field, secondary_field].includes(key)) {
      toggleField(fields[key], fields[key].alt);
    }
  }

  login() {
    if (
      (this.dpa_check == 'dob' && this.loginForm.value.dob != '') ||
      (this.dpa_check == 'postcode' && this.loginForm.value.postcode != '')
    ) {
      this.loginBtnDisabled = true;
      this.commonService.showLoader(this.translate.instant('LOGIN.LOADING'), 2000);
      let debtor_id = localStorage.getItem('debtor_id');
      let form_slug = localStorage.getItem('form_slug');
      let dpa_pass_key;
      switch (this.dpa_check) {
        case 'dob':
          dpa_pass_key = this.loginForm.value.dob
            .split('/')
            .reverse()
            .join('-');
          if (this.datepicker_date_format == 'MM/DD/YYYY') {
            let dateSplit = this.loginForm.value.dob.split('/');
            dpa_pass_key =
              dateSplit[2] + '-' + dateSplit[0] + '-' + dateSplit[1];
          }
          break;
        case 'postcode':
          dpa_pass_key = this.loginForm.value.postcode;
          break;
      }
      this.authService
        .authenticate(debtor_id, form_slug, this.dpa_check, dpa_pass_key)
        .subscribe((res) => {
          this.commonService.dismissLoader();
          if (res['result']) {
            this.authService.remote_token = res['data'];

            localStorage.removeItem('before_login_completed');

            let settings = JSON.parse(localStorage.getItem('portal_settings'));
            let hasAfterLoginNotifications = false;

            if (
              settings?.additional_settings?.after_login?.multistep_notification?.main_content?.textarea 
              && settings?.additional_settings?.after_login?.after_login_allow_multistep === "yes" 
            ) {
              hasAfterLoginNotifications = true;
            }
            else if (settings?.additional_settings?.custom_notifications?.after_login) {
              hasAfterLoginNotifications = true;
            }

            if (hasAfterLoginNotifications) {
              localStorage.setItem('is_login', '1');

              let navigationExtras: NavigationExtras = {
                queryParams: {
                  url: '/home',
                },
              };

              if (
                settings?.additional_settings?.after_login?.multistep_notification?.main_content?.textarea ||
                settings?.additional_settings?.after_login?.after_login_allow_multistep === "yes"
              ) {
                this.router.navigate(['/login/after-notifications'], navigationExtras);
              } else {
                this.router.navigate(['/login/terms'], navigationExtras);
              }
            }
            else {
              localStorage.setItem('remote_token', res['data']);
              let navigationExtras: NavigationExtras = {
                queryParams: {
                  fslug: localStorage.getItem('form_slug'),
                  cid: localStorage.getItem('case_id'),
                  did: localStorage.getItem('debtor_id'),
                  dpa: localStorage.getItem('dpa_check'),
                },
                replaceUrl: true,
              };
              localStorage.setItem('dynamicLogin', this.dynamicLogin.toString());
              this.router.navigate(['/home'], navigationExtras);
            }
          } else {
            this.commonService.showToast(this.translate.instant('LOGIN.INVALID_DATE_OF_BIRTH'), 'danger');
            this.commonService.showToast(res['message'], 'danger');
            this.loginBtnDisabled = false;
          }
        });
    }
  }

  addSlash(e) {
    if (e.which !== 8) {
      let length = this.loginForm.value.dob.length;
      if (length === 2 || length === 5) {
        let dob = this.loginForm.value.dob + '/';
        this.loginForm.controls['dob'].patchValue(dob);
      }
    }
  }

  login_direct() {
    if (this.agreement_acceptance && !this.isAgreed) {
      this.commonService.showToast(this.translate.instant('LOGIN.PLEASE_ACCEPT_AGREEMENT'), 'danger');
      return;
    }

    let data = this.loginForm.value;
    let filtered_data = {};
    Object.keys(data).filter((value) =>
      data[value] != ''
        ? value == 'dob'
          ? (filtered_data[value] = data[value].split('/').reverse().join('-'))
          : (filtered_data[value] = data[value])
        : ''
    );
    if ('dob' in filtered_data) {
      let dateSplit = this.loginForm.value.dob.split('/');
      Object.assign(filtered_data, {
        dob: dateSplit[2] + '-' + dateSplit[0] + '-' + dateSplit[1],
      });
    }
    if (Object.keys(filtered_data).length >= 2) {
      this.loginBtnDisabled = true;
      this.commonService.showLoader(this.translate.instant('LOGIN.LOADING'), 2000);
      this.authService.authenticate_direct(filtered_data).subscribe((res) => {
        this.commonService.dismissLoader();
        if (res['result']) {
          // console.log('Case Ref:', filtered_data['case_ref']);
          
          const debtorId = res['data']['debtor_id'];
          // Store the initial auth data
          this.authService.remote_token = res['data']['remote_token'];
          localStorage.setItem('case_id', res['data']['case_id']);
          this.authService.case_id = res['data']['case_id'];
          localStorage.setItem('debtor_id', debtorId);
          this.authService.debtor_id = debtorId;
          localStorage.setItem(
            'default_webform_configured',
            res['data']['default_webform_configured']
          );
          this.authService.default_webform_configured =
            res['data']['default_webform_configured'];
          localStorage.setItem('dynamicLogin', this.dynamicLogin.toString());
          this.authService.dynamicLogin = this.dynamicLogin.toString();
          
          // Check if client is oriel before sending OTP
          if (this.caseService.isClient('oriel')) {
            // Send OTP only for oriel client
            const caseRef = filtered_data['case_ref'];
            const debtorEmail = filtered_data['debtor_email'];
            
            this.commonService.showLoader(this.translate.instant('SENDING_OTP'));
            this.authService.sendOtpForDebtorLogin(data).subscribe(
              (otpResponse: any) => {
                console.log('OTP Response:', otpResponse);
                this.commonService.dismissLoader();
                if (otpResponse?.result) {
                  // Navigate to OTP verification page with necessary data
                  this.router.navigate(['/otp-verification'], {
                    state: {
                      debtorEmail: debtorEmail,
                      caseId: res['data']['case_id'],
                      remoteToken: res['data']['remote_token']
                    },
                    replaceUrl: true // This will replace the current route in history
                  });
                  return; // Exit the function after navigation
                } else {
                  if (otpResponse?.message) {
                    this.showErrorAlert('Email Error', otpResponse?.message);
                  } else {
                    this.commonService.showToast(
                      'Failed to send OTP. Please try again.',
                      'danger'
                    );
                  }
                }
              }
            );
            return;
          } else {
            // For non-oriel clients, continue with normal flow
            this.reinitService.triggerReinit();
            this.router.navigate(['/home']);
          }

          localStorage.removeItem('before_login_completed');

          let settings = JSON.parse(localStorage.getItem('portal_settings'));
          let hasAfterLoginNotifications = false;

          if (
            settings?.additional_settings?.after_login?.multistep_notification?.main_content?.textarea
            && settings?.additional_settings?.after_login?.after_login_allow_multistep === "yes"
          ) {
            hasAfterLoginNotifications = true;
          }
          else if (settings?.additional_settings?.custom_notifications?.after_login) {
            hasAfterLoginNotifications = true;
          }

          if (hasAfterLoginNotifications) {
            localStorage.setItem('is_login', '1');
            let navigationExtras: NavigationExtras = {
              queryParams: {
                url: res['data']['default_webform_configured'] ? '/home' : '/main',
              },
            };

            if (
              settings?.additional_settings?.after_login?.multistep_notification?.main_content?.textarea ||
              settings?.additional_settings?.after_login?.after_login_allow_multistep === "yes"
            ) {
              this.router.navigate(['/login/after-notifications'], navigationExtras);
            } else {
              this.router.navigate(['/login/terms'], navigationExtras);
            }
          }
          else {
            if (res['data']['default_webform_configured']) {
              localStorage.setItem('form_slug', res['data']['form_slug']);
              this.authService.form_slug = res['data']['form_slug'];
              localStorage.setItem('dpa_check', res['data']['dpa_check']);
              this.authService.dpa_check = res['data']['dpa_check'];
              let navigationExtras: NavigationExtras = {
                queryParams: {
                  fslug: res['data']['form_slug'],
                  cid: res['data']['case_id'],
                  did: res['data']['debtor_id'],
                  dpa: res['data']['dpa_check'],
                },
                replaceUrl: true,
              };
              this.router.navigate(['/home'], navigationExtras);
            } else {
              localStorage.setItem('remote_token', res['data']['remote_token']);
              localStorage.setItem('is_login', '1');
              this.router.navigate(['/main'], {
                replaceUrl: true,
              });
            }
          }
        } else {
          console.log("no hola? 4");
          this.loginBtnDisabled = false;
          this.commonService.showToast(res['message'], 'danger');
        }
      });
    }
  }

  fetchSettings() {
    this.authService.portalSettings2().subscribe((res: any) => {
      const portalSettings = res.data;
      localStorage.setItem('portal_settings', JSON.stringify(portalSettings));
      this.applyPortalSettings(portalSettings);
    });
  }

  applyPortalSettings(portalSettings: any) {
    // Process dynamic field labels
    if (portalSettings.dynamic_field) {
      Object.keys(portalSettings.dynamic_field).forEach(elementKey => {
        if (!portalSettings.dynamic_field[elementKey].label) {
          portalSettings.dynamic_field[elementKey].label = this.toCamelCase(portalSettings.dynamic_field[elementKey].key);
        }
      });
    }

    this.checkTranslation = !!localStorage.getItem('available_languages');

    // Apply theme
    if (portalSettings?.theme_settings) {
      this.applyTheme(portalSettings.theme_settings);
    }

    // Dynamic login fields
    if (this.dynamicLogin && portalSettings.dynamic_field) {
      if (Object.keys(portalSettings.dynamic_field || {}).length >= 2) {
        this.fieldList = portalSettings.dynamic_field;
        this.fieldKeys = Object.keys(this.fieldList);
        this.showMultipleFieldsMessage = this.fieldKeys.length > 2;

        const swap = portalSettings.allow_swapping_fields;
        this.swapFields = (swap?.primary_field && swap?.secondary_field) ? {
          primary_field: swap.primary_field,
          secondary_field: swap.secondary_field,
          secondary_field_label: swap.secondary_field_label || 
                                  this.defaultLabels[swap.secondary_field] || 
                                  this.toCamelCase(swap.secondary_field),
        } : {
          primary_field: 'case_ref',
          secondary_field: 'case_id',
          secondary_field_label: this.defaultLabels['case_id'],
        };
        this.updateField();
      }

      // Show help icon if any field has content
      if (this.fieldKeys && this.fieldList) {
        this.showInfoIcon = this.fieldKeys.some(key => this.fieldList[key]?.content != '');
      }
    }

    // Portal details
    const pd = portalSettings?.portal_details;
    if (pd?.title) {
      this.title = pd.title;
      const domain = new URL(localStorage.getItem('server_url')).hostname.split('.')[0];
      this.client = domain.charAt(0).toUpperCase() + domain.slice(1);
    }

    this.logo_url = pd?.login_logo_url || pd?.logo_url || '';
    this.background_url = pd?.background_url || '';
    this.datepicker_date_format = portalSettings?.datepicker_date_format || 'DD/MM/YYYY';
    this.showNotification = portalSettings?.additional_settings?.custom_notifications?.show_notification_panel || false;
    this.login_header_message = pd?.login_header_message || '';
    this.login_footer_colour_code = pd?.login_footer_colour_code || '';
    this.agreement_acceptance = pd?.agreement_acceptance_checkbox || false;

    // Language-specific settings
    const lang = localStorage.getItem('language');
    const langSuffix = (lang && lang != 'en') ? ('_' + lang) : '';
    
    this.agreement_acceptance_label = pd?.['agreement_acceptance_label' + langSuffix] || pd?.agreement_acceptance_label || '';
    this.login_footer_message = pd?.['login_footer_message' + langSuffix] || pd?.login_footer_message || '';
    this.full_title_login = pd?.['full_title' + langSuffix] || pd?.full_title || null;
    this.login_button_label = pd?.['login_button_label' + langSuffix] || pd?.login_button_label || '';
    
    const sd = portalSettings?.additional_settings?.state_disclosures;
    this.state_disclosures = langSuffix ? 
      (sd?.['state_disclosures' + langSuffix] || sd?.state_disclosures || '') : 
      (sd?.state_disclosures || '');

    this.logo_size = pd?.logo_size || '60px';
  }

  hasStateDisclosures(): boolean {
    if (!this.state_disclosures) return false;
    const content = this.state_disclosures.replace(/<[^>]*>/g, '').trim();
    return content !== '';
  }

  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: HelpContentComponent,
      event: ev,
      translucent: true,
      componentProps: {
        fieldList: this.fieldList,
        fieldKeys: this.fieldKeys
      }
    });
    await popover.present();

    const { role } = await popover.onDidDismiss();
  }

  toCamelCase(str) {
    str = str.replace(/_/g, ' ');
    str = str.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
        return word.toUpperCase();
    });
    str = str.charAt(0).toUpperCase() + str.slice(1);
    return str;
}

  async forgotLoginDetails(){
    const alert = await this.alertController.create({
        header: this.translate.instant('LOGIN.ENTER_DETAILS_BELOW'),
        message: '',
        inputs: [
          { name: 'ssn',
            type: 'number',
            placeholder: this.translate.instant('LOGIN.LAST_FOUR_DIGITS_SSN'),
            attributes: {
              maxlength: 4,
              min: 1
            },
          },
          {
            name: 'surname',
            type: 'text',
            placeholder: this.translate.instant('LOGIN.LAST_NAME'),
          }
        ],
        buttons: [
          {
            text: this.translate.instant('COMMON.CANCEL'),
            role: 'cancel',
            handler: () => {
              return ;
            },
          },
          {
            text: this.translate.instant('COMMON.PROCEED'),
            role: 'OK',
            handler:  async(data) => {
              await this.getForgotLoginDetails(data);
            },
          },
        ],
      });

      await alert.present();
  }

  async generateListHTML(data: any[]) {
  let html = '<ion-grid>';

  html += `
    <ion-row class="ion-text-uppercase ion-text-sm">
      <ion-col size="1"><strong>${this.translate.instant('LOGIN.CASE')}</strong></ion-col>
      <ion-col size="6"><strong>${this.translate.instant('LOGIN.ID')}</strong></ion-col>
      <ion-col size="5"><strong>${this.translate.instant('LOGIN.REF')}</strong></ion-col>
    </ion-row>
  `;

  data.forEach((item, index) => {
    html += `
      <ion-row class="ion-text-sm">
        <ion-col size="1">${index + 1}</ion-col>
        <ion-col size="6">${item.id}</ion-col>
        <ion-col size="5">${item.ref}</ion-col>
      </ion-row>
    `;
  });

  html += '</ion-grid>';
  return html;
}
  async getForgotLoginDetails(data){
    this.commonService.showLoader();
    this.authService.getForgotLoginDetails(data).subscribe(async (res)=>{
      this.commonService.dismissLoader();
      if(res['result'] && res['data']){
        const secondAlert = await this.alertController.create({
          header: this.translate.instant('LOGIN.LOGIN_CASE_DETAILS'),
          message: await this.generateListHTML(res['data']),
          cssClass: 'custom-forgot-alert',
          buttons: [this.translate.instant('COMMON.CLOSE')]
        });
        await secondAlert.present();
      }else{
        this.commonService.showToast(res['message'],'danger');
      }

    });
  }

  async showStatementDisclosures() {
    const settings = JSON.parse(localStorage.getItem('portal_settings'));
    const language = localStorage.getItem('language') || 'en';
    const state_disclosures_panel = settings?.additional_settings?.state_disclosures;
    let message = '';

    if (language != 'en') {
      message = state_disclosures_panel?.['state_disclosures_' + language] || state_disclosures_panel?.state_disclosures || '';
    }
    else {
      message = state_disclosures_panel?.state_disclosures || '';
    }

    const alert = await this.alertController.create({
      header: this.translate.instant('LOGIN.STATE_DISCLOSURES'),
      message: '<div style="overflow-y:scroll !important;max-height:60vh;">' + message + '</div>',
      cssClass: 'state-disclosures-modal',
      buttons: [
        {
          text: this.translate.instant('COMMON.CLOSE'),
          handler: () => {
            return true;
          }
        }
      ]
    });

    return await alert.present();
  }

  // CORE1-5703 => Method for adding custom scss variables
  applyTheme(themeSettings: any) {
    const root = document.documentElement;

    // Iterate through the theme settings and set CSS variables
    for (const panelKey in themeSettings) {
      if (themeSettings.hasOwnProperty(panelKey)) {
        const colors = themeSettings[panelKey];
        for (const colorKey in colors) {
          if (colors.hasOwnProperty(colorKey)) {
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            const target = isDark ? document.body : document.documentElement;
            target.style.setProperty(`--${colorKey.replace(/_/g, '-')}`, colors[colorKey]);
          }
        }
      }
    }
  }

  private wrapFooterAndSiblings() {
    const footers = document.querySelectorAll('master-footer');
    footers.forEach(footer => {
      const parent = footer.parentElement;
      if (!parent) return;
      if (parent.classList.contains('page-container')) return;

      const wrapper = document.createElement('div');
      wrapper.classList.add('page-container');

      const siblings = Array.from(parent.children);
      siblings.forEach(child => wrapper.appendChild(child));

      parent.appendChild(wrapper);
    });
  }

  openHelpPage() {
    window.open('https://orielcollections.co.uk/contact-us/', '_blank');
  }

  private async showErrorAlert(header: string, message: string) {
    const alert = await this.alertController.create({
      header: header,
      message: message,
      cssClass: 'custom-form-alert',
      buttons: [
        {
          text: this.translate.instant('COMMON.CANCEL'),
          role: 'cancel',
          handler: () => { },
        },
      ],
    });
    await alert.present();
  }
}
